import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const GOLD = '#c9a96e';
const DATE_LOCALES = { fr, en: enUS };

function formatDate(dateStr, lang = 'en') {
  if (!dateStr) return '-';
  try {
    const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
    return format(d, 'dd MMM yyyy', { locale: DATE_LOCALES[lang] || enUS });
  } catch {
    return '-';
  }
}

function getUrgencyColor(days) {
  if (days == null) return 'default';
  if (days <= 7) return '#ef4444';
  if (days <= 30) return '#f59e0b';
  return '#22c55e';
}

const UpcomingDeadlines = ({ deadlines }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const lang = i18n.language;

  if (deadlines.loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={44} sx={{ borderRadius: '8px', mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  const items = deadlines.data?.deadlines || [];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              color: GOLD,
              letterSpacing: '0.02em',
            }}
          >
            {t('clmDashboard.upcomingDeadlines', 'Upcoming Deadlines')}
          </Typography>
          {deadlines.data && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
            >
              {deadlines.data.total_expiring_30} {t('clmDashboard.within30days', 'within 30 days')}
            </Typography>
          )}
        </Box>

        {items.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, textAlign: 'center', py: 3 }}
          >
            {t('clmDashboard.noDeadlines', 'No upcoming deadlines')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {items.slice(0, 8).map((item) => {
              const urgencyColor = getUrgencyColor(item.days_until_expiration);
              return (
                <Box
                  key={item.contract_id}
                  onClick={() => navigate(`/contract/${item.contract_id}`)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': {
                      background: alpha(theme.palette.action.hover, 0.06),
                    },
                  }}
                >
                  {/* Urgency dot */}
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: urgencyColor,
                      flexShrink: 0,
                    }}
                  />

                  {/* Title */}
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: 500,
                      fontSize: '0.82rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.title}
                  </Typography>

                  {/* Auto-renewal badge */}
                  {item.auto_renewal && (
                    <Chip
                      label={t('clmDashboard.autoRenew', 'Auto')}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        bgcolor: alpha('#f59e0b', 0.1),
                        color: '#f59e0b',
                        border: `1px solid ${alpha('#f59e0b', 0.2)}`,
                      }}
                    />
                  )}

                  {/* Days remaining */}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: urgencyColor,
                      fontSize: '0.72rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.days_until_expiration != null
                      ? `${item.days_until_expiration}${t('clmDashboard.daysShort', 'd')}`
                      : '-'}
                  </Typography>

                  {/* Expiration date */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      minWidth: 75,
                      textAlign: 'right',
                    }}
                  >
                    {formatDate(item.expiration_date || item.renewal_date, lang)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingDeadlines;
